/**
 * dashboard/reports/compose.tsx — Tutor compose report page (N-18).
 *
 * Lives at /dashboard/reports/compose. Tutor-only route that wraps the
 * <ReportComposer> with a class selector + preview shell. URL search
 * param `classId` (optional) pre-selects the class.
 */

import { createFileRoute, redirect, useSearch, Link } from '@tanstack/react-router';
import { z } from 'zod';
import type { ReactNode } from 'react';
import { PageHeader } from '../../../components/ui/PageHeader.js';
import { ReportComposer } from '../../../components/tutor/ReportComposer.js';
import { freshSessionQuery } from '../../../lib/api/queries.js';

const searchSchema = z.object({
  classId: z.string().optional(),
});

export const Route = createFileRoute('/dashboard/reports/compose')({
  validateSearch: (search) => searchSchema.parse(search),
  beforeLoad: async ({ context, location }) => {
    const session = await context.queryClient.fetchQuery(freshSessionQuery);
    const user = session?.ok ? (session.data?.user ?? null) : null;
    if (!user) {
      throw redirect({
        to: '/login',
        search: { error: undefined, redirect: location.href },
      });
    }
    if (user.role === 'admin') {
      throw redirect({ to: '/admin' });
    }
    if (user.role !== 'tutor' || !user.isApprovedTutor) {
      throw redirect({ to: '/me' });
    }
    return null;
  },
  component: ReportComposePage,
});

function ReportComposePage(): ReactNode {
  const search = useSearch({ from: '/dashboard/reports/compose' });
  const classId = search.classId ?? '';

  return (
    <main className="page-shell" data-testid="report-compose-page">
      <PageHeader
        eyebrow="Rapor orang tua"
        title="Buat rapor progres"
        description="Kirim rapor progres siswa ke orang tua via email dan WhatsApp."
        action={
          <Link to="/dashboard/reports/history" className="button button-secondary">
            Riwayat rapor
          </Link>
        }
      />

      {classId ? (
        <ReportComposer classId={classId} />
      ) : (
        <div className="report-compose-class-picker">
          <p className="form-message">
            Untuk mulai, buka halaman detail kelas dan klik "Buat rapor".
            Halaman ini menerima parameter <code>?classId=&lt;id&gt;</code>.
          </p>
          <Link to="/dashboard" className="button button-primary">
            Pilih kelas
          </Link>
        </div>
      )}
    </main>
  );
}